/**
 * Reseller management use cases. Each one validates input and routes to the
 * repository — the domain predicates (canCredit, canSuspend, ...) live in
 * @domain/reseller/Reseller.
 */
import {
  canActivate,
  canCredit,
  canSuspend,
  canTerminate,
  type ResellerDetail,
} from '@domain/reseller/Reseller';
import type { HttpResellerRepository } from '@infrastructure/api/HttpResellerRepository';

export class InvalidResellerOp extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidResellerOp';
  }
}

export const creditReseller = (repo: HttpResellerRepository) =>
  async (
    reseller: ResellerDetail,
    amount: string,
    description: string,
  ): Promise<ResellerDetail> => {
    if (!canCredit(reseller)) {
      throw new InvalidResellerOp(
        `cannot credit a reseller in status="${reseller.status}"`,
      );
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      throw new InvalidResellerOp('amount must be a positive number');
    }
    const desc = (description ?? '').trim();
    if (!desc) throw new InvalidResellerOp('description required for audit');
    return repo.credit(reseller.id, amount, desc);
  };

export const suspendReseller = (repo: HttpResellerRepository) =>
  async (reseller: ResellerDetail, reason: string): Promise<ResellerDetail> => {
    if (!canSuspend(reseller)) {
      throw new InvalidResellerOp(`cannot suspend status=${reseller.status}`);
    }
    const r = (reason ?? '').trim();
    if (!r) throw new InvalidResellerOp('reason required for suspend');
    return repo.suspend(reseller.id, r);
  };

export const activateReseller = (repo: HttpResellerRepository) =>
  async (reseller: ResellerDetail): Promise<ResellerDetail> => {
    if (!canActivate(reseller)) {
      throw new InvalidResellerOp(
        `cannot activate from status=${reseller.status}`,
      );
    }
    // pending → activate, suspended → reactivate
    return reseller.status === 'suspended'
      ? repo.reactivate(reseller.id)
      : repo.activate(reseller.id);
  };

export const terminateReseller = (repo: HttpResellerRepository) =>
  async (reseller: ResellerDetail, reason: string): Promise<ResellerDetail> => {
    if (!canTerminate(reseller)) {
      throw new InvalidResellerOp('already terminated');
    }
    const r = (reason ?? '').trim();
    if (!r) throw new InvalidResellerOp('reason required for terminate');
    return repo.terminate(reseller.id, r);
  };
